import * as React from "react";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import config from "../../Config/config.json";
import { useState, useRef, useEffect } from "react";

interface ImageItem {
  img: string;
  title?: string;
}

interface WovenImageListProps {
  data: any[];
  cols?: number;
  gap?: number;
  width?: number | string;
  height?: number | string;
}

const WSWovenImageList: React.FC<WovenImageListProps> = ({
  data,
  cols = 3,
  gap = 8,
  width = 500,
  height = 450,
}) => {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [selectedImg, setSelectedImg] = useState<string | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [startDrag, setStartDrag] = useState({ x: 0, y: 0 });
  const [origin, setOrigin] = useState({ x: "50%", y: "50%" });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setScale(1);
      setPos({ x: 0, y: 0 });
      setOrigin({ x: "50%", y: "50%" });
    }
  }, [open]);

  const clampPosition = (x: number, y: number, newScale: number) => {
    if (!containerRef.current || !imgRef.current) return { x, y };

    const container = containerRef.current.getBoundingClientRect();
    const img = imgRef.current.getBoundingClientRect();

    const naturalWidth = imgRef.current.naturalWidth;
    const naturalHeight = imgRef.current.naturalHeight;

    // dimensions of scaled image
    const scaledWidth = naturalWidth * newScale;
    const scaledHeight = naturalHeight * newScale;

    const maxX = Math.max((scaledWidth - container.width) / 2, 0);
    const maxY = Math.max((scaledHeight - container.height) / 2, 0);

    return {
      x: Math.min(Math.max(x, -maxX), maxX),
      y: Math.min(Math.max(y, -maxY), maxY),
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale <= 1) return;
    setIsDragging(true);
    setStartDrag({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const newX = e.clientX - startDrag.x;
    const newY = e.clientY - startDrag.y;
    setPos(clampPosition(newX, newY, scale));
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    const zoomStep = 0.1;
    let newScale = scale;

    if (e.deltaY < 0) {
      newScale = Math.min(scale + zoomStep, 5);
    } else {
      newScale = Math.max(scale - zoomStep, 1);
    }

    setOrigin({
      x: `${(offsetX / rect.width) * 100}%`,
      y: `${(offsetY / rect.height) * 100}%`,
    });

    setScale(newScale);

    if (newScale === 1) {
      setPos({ x: 0, y: 0 });
    } else {
      setPos(clampPosition(pos.x, pos.y, newScale));
    }
  };

  const handleOpen = (img: any) => {
    setSelectedImg(img);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImg(null);
  };

  return (
    <>
      <ImageList
        sx={{
          display: "flex",
          flexDirection: "row",
          overflowX: "auto",
          width,
          height,
          gap: 1,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            height: 6,
          },
        }}
      >
        {data.map((item, index) => (
          <ImageListItem
            key={index}
            onClick={() => handleOpen(item)}
            sx={{ flex: "0 0 auto", width: 160 }}
          >
            <img
              src={`${config.imgUrl}${item}`}
              alt={item.title || `Image-${index}`}
              loading="lazy"
              style={{
                cursor: "pointer",
                // objectFit: "cover",
                 objectFit: "fill", 
                width: "100%",
                height: "100%",
                borderRadius: 8,
                border:'1px solid black'
              
              }}
            />
          </ImageListItem>
        ))}
      </ImageList>

      {/* Enlarged Image Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth={false}>
        <div
          ref={containerRef}
          style={{
            position: "relative",
            width: "90vw",
            height: "90vh",
            background: "black",
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          onWheel={handleWheel}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "white",
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>

          {selectedImg && (
            <img
              ref={imgRef}
              src={`${config.imgUrl}${selectedImg}`}
              alt="zoomable"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{
                transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                transformOrigin: `${origin.x} ${origin.y}`,
                cursor:
                  isDragging ? "grabbing" : scale > 1 ? "grab" : "default",
                transition: isDragging ? "none" : "transform 0.2s ease",
                maxWidth: "100%",
                maxHeight: "100%",
                userSelect: "none",
                pointerEvents: "all",
              }}
              draggable={false}
            />
          )}
        </div>
      </Dialog>
    </>
  );
};

export default WSWovenImageList;
